// WeightHistoryChart — the §4 trend line for recorded weights (Design
// Constitution §4 Charts, D03, D04, A26).
//
//   • one 2.5px brand-green line with points; past points hollow, the latest
//     solid; a baseline only (no grid, no area fill);
//   • gray min/max weight labels and gray first/last dates;
//   • a white bordered card (date, weight, change) for the active point. A
//     point activates on hover, focus and tap, and Escape clears it (D04, D09);
//     the same facts are always in the record's table, so the card is never
//     the only place they live.
//
// The pig breeder chart (src/pig/SowsView.tsx, BreederWeightHistoryChart) is
// the shape this generalizes; it stays where it is in its own lane.

import React from 'react';
import './weightHistoryChart.css';

export type WeightHistoryPoint = {
  key: string;
  /** ISO date (YYYY-MM-DD) of the weigh-in. */
  date: string;
  weight: number;
  /** Gray line under the weight in the card, e.g. "+12 lb since previous weight". */
  detail?: string;
};

type WeightHistoryChartProps = {
  points: WeightHistoryPoint[];
  fmtDate: (date: string) => React.ReactNode;
  /** Accessible name for the figure. */
  label?: string;
};

const W = 640;
const X0 = 52;
const X1 = 588;
const Y_TOP = 28;
const Y_BASE = 156;

export default function WeightHistoryChart({
  points: input,
  fmtDate,
  label = 'Weight over time',
}: WeightHistoryChartProps) {
  const [active, setActive] = React.useState<number | null>(null);
  const points = input
    .map((p) => ({...p, time: Date.parse(p.date + 'T12:00:00')}))
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.weight))
    .sort((a, b) => a.time - b.time);
  if (points.length === 0) return null;

  const min = Math.min(...points.map((p) => p.weight));
  const max = Math.max(...points.map((p) => p.weight));
  const first = points[0].time;
  const last = points[points.length - 1].time;
  const plot = points.map((p, i) => ({
    ...p,
    x:
      points.length === 1
        ? (X0 + X1) / 2
        : X0 + (X1 - X0) * (last === first ? i / (points.length - 1) : (p.time - first) / (last - first)),
    y: max === min ? (Y_TOP + Y_BASE) / 2 : Y_BASE - ((Y_BASE - Y_TOP) * (p.weight - min)) / (max - min),
  }));
  const number = (value: number) => value.toLocaleString(undefined, {maximumFractionDigits: 1});
  const activePoint = active == null ? null : plot[active] || null;

  return (
    <figure className="wcf-weight-chart" aria-label={label} data-weight-chart="1">
      <svg viewBox={`0 0 ${W} 190`} role="img" aria-label={label}>
        <line x1={X0} y1={Y_BASE} x2={X1} y2={Y_BASE} stroke="var(--border)" />
        <text x={X0 - 8} y={Y_TOP + 4} textAnchor="end" fill="var(--text-2)" fontSize="12">
          {number(max)}
        </text>
        {max !== min && (
          <text x={X0 - 8} y={Y_BASE + 4} textAnchor="end" fill="var(--text-2)" fontSize="12">
            {number(min)}
          </text>
        )}
        {plot.length > 1 && (
          <polyline
            points={plot.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="var(--brand)"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {plot.map((p, i) => (
          <g key={p.key}>
            <circle
              cx={p.x}
              cy={p.y}
              r={active === i ? 5.5 : 4}
              fill={i === plot.length - 1 ? 'var(--brand)' : 'var(--surface)'}
              stroke="var(--brand)"
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              className="wcf-weight-chart-point"
              cx={p.x}
              cy={p.y}
              r="18"
              fill="transparent"
              role="button"
              tabIndex={0}
              aria-label={`${p.date}: ${number(p.weight)} lb`}
              aria-pressed={active === i}
              data-weight-chart-point={p.key}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setActive(i);
                } else if (event.key === 'Escape') setActive(null);
              }}
            />
          </g>
        ))}
        <text x={X0} y="182" fill="var(--text-2)" fontSize="12">
          {fmtDate(points[0].date)}
        </text>
        {points.length > 1 && (
          <text x={X1} y="182" textAnchor="end" fill="var(--text-2)" fontSize="12">
            {fmtDate(points[points.length - 1].date)}
          </text>
        )}
      </svg>
      {activePoint && (
        <figcaption className="wcf-weight-chart-card" aria-live="polite" data-weight-chart-card="1">
          <strong>{fmtDate(activePoint.date)}</strong>
          <span>{number(activePoint.weight)} lb</span>
          {activePoint.detail && <span>{activePoint.detail}</span>}
        </figcaption>
      )}
    </figure>
  );
}
